<template>
	<div
		class="table-mapper-field-settings field-settings needs-data-source mb-9"
		:class="{ 'has-data-source': selectedNodeUUID }"
		v-if="localField"
	>
		<div class="settings-description">The table mapper lets users map columns in a table to predefined properties.</div>
		<ol class="field-setting-section">
			<li class="configuration-step choose-data-source">
				<div class="step-title">Choose data source</div>
				<div class="step-sub-title">This is the table the user will map</div>
				<CombinedInputFieldDataMenu
					:value.sync="selectedSource"
					readonly
					:textAreaMode="false"
					:attachTo="'.choose-data-source'"
					:multipleSelection="false"
					:valuesTypes="dataInputMenuValueTypes"
				/>
				<div class="warning-message" v-if="!selectedNodeHasTestValues">
					You need to test the Data Source in order to fill the placements
				</div>
			</li>
			<li class="configuration-step">
				<div class="step-title">Create predefined properties</div>
				<div class="step-sub-title">These are the properties the user will be able to map to</div>
				<AppTabSelector :tabItems="tabItems" v-model="mode" />
				<div class="tab-option pt-2">
					<MultipleSelectSettingsEditor
						v-if="isManualMode"
						:options="dropdownOptions"
						:isOptionValue="false"
						keyPlaceholder="Enter destination header..."
						addOptionText="Add a property"
						:defaultOptionTemplate="defaultOptionTemplate"
						@onChange="(v) => (dropdownOptions = v)"
					/>
					<MultipleSelectTableSourceSettingsEditor
						v-if="isTableMode"
						:localField="localField"
						@updateLocalField="({ key, value }) => setLocalFieldKey(key, value)"
					/>
				</div>
			</li>

			<li class="configuration-step">
				<div class="step-title">Add a title</div>
				<BasicTextField placeholder="Map your data" v-model="miniAppTitle" />
			</li>
			<v-expansion-panels v-model="panel" flat accordion>
				<ExpansionPanel :title="'Advanced'">
					<v-row no-gutters class="align-center mb-4">
						<v-col cols="12">
							<h3 class="reg">
								<span class="field-text">Output mode</span>
							</h3>
						</v-col>
					</v-row>
					<v-row no-gutters class="align-center">
						<v-col cols="auto">
							<AppSelect naked dense hide-details v-model="outputMode" :items="outputModeOptions" />
						</v-col>
					</v-row>
				</ExpansionPanel>
			</v-expansion-panels>
		</ol>
	</div>
</template>

<script>
const MAPPING_OUTPUT = 'MAPPING_OUTPUT';
const HEADERS_MAPPING = 'HEADERS_MAPPING';

import { mapGetters, mapActions } from 'vuex';

import ParamsService from '@/utils/ParamsService';

import ExpansionPanel from '../WidgetSettings/ExpansionPanel';
import CombinedInputFieldDataMenu from '@/components/DataInput/CombinedInputFieldDataMenu';
import MultipleSelectSettingsEditor from '../FieldOptions/MultipleSelectSettingsEditor/MultipleSelectSettingsEditor';
import MultipleSelectTableSourceSettingsEditor from '../FieldOptions/MultipleSelectTableSourceSettingsEditor';
import FieldSettingsMixin from '../FieldSettingsMixin';
import TableOnlyAllowedValueTypesMixin from '../ValueTypesMixins/TableOnlyAllowedValueTypesMixin';
import TableRelatedFieldSettingsMixin from '../TableRelatedFieldSettingsMixin';
import { getPreviousStepDataForNodeByUUID } from '@/store/modules/nodes/getters';
import { nodesDataByUUID, replaceInsertDataStringDictionary } from '@/store/modules/selectedWorkflow/getters';
import { configurationsMappingBlocksUUIDs } from '@/store/modules/selectedWorkflow/modules/configurations/getters';

export default {
	name: 'TableMapperFieldSettings',
	components: {
		ExpansionPanel,
		CombinedInputFieldDataMenu,
		MultipleSelectSettingsEditor,
		MultipleSelectTableSourceSettingsEditor,
	},
	mixins: [FieldSettingsMixin, TableOnlyAllowedValueTypesMixin, TableRelatedFieldSettingsMixin],
	computed: {
		...mapGetters([
			nodesDataByUUID.fullPath,
			getPreviousStepDataForNodeByUUID.fullPath,
			replaceInsertDataStringDictionary.fullPath,
			configurationsMappingBlocksUUIDs.fullPath,
		]),
		...mapGetters('widgetEditor', ['widgetMetadata']),

		dropdownOptions: {
			get() {
				const options = this.localField.metadata?.options?.columns_options || [];
				// this is hack - required should be retured from the server
				return options.map((opt) => ({ ...this.defaultOptionTemplate, ...opt }));
			},
			set(value) {
				const formattedColumnOptions = value.map((valObj) => ({ ...valObj, value: valObj.value || valObj.text }));
				this.setLocalFieldKey('metadata.options.columns_options', formattedColumnOptions);
			},
		},
		selectedNodeUUID() {
			return ParamsService.extractNodeUUIDFromValue(this.selectedSource);
		},
		selectedPath() {
			return ParamsService.extractPathFromValue(
				this.selectedSource,
				this[replaceInsertDataStringDictionary.fullPath],
				this[configurationsMappingBlocksUUIDs.fullPath]
			);
		},
		selectedSource: {
			get() {
				return this.localField.metadata.options.step_id.value;
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.step_id', { value });
			},
		},
		selectedHeaders: {
			get() {
				return this.localField.metadata.options.headers || [];
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.headers', value);
			},
		},
		miniAppTitle: {
			get() {
				return this.widgetMetadata.options.title.value || '';
			},
			set(value) {
				const newValue = Object.assign({ ...this.widgetMetadata.options.title }, { value, active: true });
				this.setWidgetOption({ key: 'title', value: newValue });
			},
		},
		headersMapping: {
			get() {
				return !!this.localField.metadata?.options?.headers_mapping_mode.enabled;
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.headers_mapping_mode', { enabled: value });
				// this.setWidgetOption({ key: 'headers_mapping_mode', value: { enabled: value } });
			},
		},
		outputMode: {
			get() {
				return this.headersMapping ? HEADERS_MAPPING : MAPPING_OUTPUT;
			},
			set(value) {
				this.headersMapping = value === HEADERS_MAPPING;
			},
		},
		selectedNodeData() {
			return this[nodesDataByUUID.fullPath][this.selectedNodeUUID];
		},
		selectedNodePreviousStepsData() {
			return this[getPreviousStepDataForNodeByUUID.fullPath](this.selectedNodeUUID) || {};
		},
		selectedNodeHasTestValues() {
			return Object.keys(this.selectedNodeData ? this.selectedNodeData.test_result || {} : {}).length > 0;
		},
		userSelection: {
			get() {
				return this.localField.metadata.options.user_selection.enabled;
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.user_selection.enabled', value);
				if (!value) {
					this.multipleSelection = false;
					this.requiredEnabled = false;
				}
			},
		},
		multipleSelection: {
			get() {
				return this.localField.metadata.options.user_selection.multiple;
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.user_selection.multiple', value);
			},
		},
		mode: {
			get() {
				return this.localField.metadata.options.dropdown_mode?.value || 0;
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.dropdown_mode.value', value);
			},
		},
		isManualMode() {
			return this.mode === 0;
		},
		isTableMode() {
			return this.mode === 1;
		},
		tableHeaders() {
			return Object.keys(this.exampleRow(this.selectedNodePreviousStepsData, this.selectedPath));
		},
	},
	methods: {
		...mapActions('widgetEditor', ['setWidgetOption']),
	},
	watch: {
		value: {
			handler(newV) {
				this.result = newV;
			},
			immediate: true,
			deep: true,
		},
	},
	data() {
		return {
			result: [],
			currentlyEdited: { currentEditedPlacement: null },
			defaultOptionTemplate: { text: '', value: '', required: true },
			tabItems: [
				{ tab: 0, title: 'Manual' },
				{ tab: 1, title: 'Table' },
			],
			outputModeOptions: [
				{
					text: 'Mapping',
					value: MAPPING_OUTPUT,
				},
				{
					text: 'Headers mapping',
					value: HEADERS_MAPPING,
				},
			],
			panel: null,
		};
	},
};
</script>

<style scoped lang="scss">
.table-mapper-field-settings {
}
</style>
